import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Moment } from 'moment/moment';
import { getDatesRange } from '@mgm/common/utils';
import { usePostHog } from 'posthog-js/react';
import { useError } from '../useCommon';
import { TIntegrateReq, TLocationsMappingReq } from '../../types/requests';
import { DorController } from '../../services';
import {
  IBaseResponse, TAxiosError, TGetDorIntegrationRes, TGetDorLocationMetrics, TGetDorLocationsRes,
} from '../../types/responses';
import { useAppDispatch, useAppSelector } from '../useAppStore';
import { getAuthenticateUser } from '../../store/authenticate/selectors';
import { QUERY_KEYS } from '../../utils/enums';
import { settingsActions } from '../../store/settings/slice';
import { ordersActions } from '../../store/orders/slice';
import { getIntegrations, getIsOpenDorDialog } from '../../store/settings/selectors';

export function useGetDorIntegrationAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();
  const integrations = useAppSelector(getIntegrations);

  return useQuery(
    [QUERY_KEYS.INTEGRATIONS.GET_DOR],
    async () => {
      const data = {
        shopName: personnel?.shopName || '',
      };

      const rsp = await DorController().getDorIntegration<TGetDorIntegrationRes>(data);

      if (rsp?.success && 'dorIntegration' in rsp) {
        dispatch(settingsActions.setDorIntegration(rsp.dorIntegration));
      } if (rsp?.error || rsp.error?.response?.status) {
        handleReqError(rsp.error);
      } if ('msg' in rsp) {
        console.error(`ERROR! get dor locations data failed! ${rsp.msg}`);
      }

      return rsp;
    },
    {
      staleTime: 3 * (60 * 1000),
      suspense: false,
      refetchOnWindowFocus: false,
      enabled: integrations?.Dor?.integrated,
      refetchOnReconnect: false,
    },
  );
}

export function useDorIntegrationAPI() {
  const { handleReqError } = useError();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const posthog = usePostHog();

  const integrate = async (data: Omit<TIntegrateReq, 'shopName'>) => {
    const payload = {
      ...data,
      shopName: personnel.shopName,
    };

    return DorController().integrate<IBaseResponse>(payload);
  };

  return useMutation(integrate, {
    onSuccess: async (rsp) => {
      if (rsp?.success) {
        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTEGRATIONS.GET_DOR] });

        posthog.capture('ManageMate_web', {
          shopName: personnel?.shopName,
          newMember: personnel?.email,
          addedAt: new Date().toISOString(),
          action: 'user_integrated_with_dor',
        });

        dispatch(settingsActions.setIsOpenIntegrationNav(false));
        setTimeout(() => {
          dispatch(settingsActions.setIsOpenDorDialog(true));
        }, 300);
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error);
      } else if ('msg' in rsp) {
        console.error(`ERROR! integrate with dor failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! integrate with dor request threw an Exception! ${error}`);
    },
  });
}

export function useGetDorLocationsAPI() {
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();
  const { handleReqError } = useError();
  const isOpenDorDialog = useAppSelector(getIsOpenDorDialog);

  return useQuery(
    [QUERY_KEYS.INTEGRATIONS.GET_DOR_LOCATIONS],
    async () => {
      const data = {
        shopName: personnel?.shopName || '',
      };

      const rsp = await DorController().getDorLocations<TGetDorLocationsRes>(data);

      if (rsp?.success && 'locations' in rsp) {
        dispatch(settingsActions.setDorLocations(rsp.locations));
      } if (rsp?.error || rsp.error?.response?.status) {
        handleReqError(rsp.error);
      } if ('msg' in rsp) {
        console.error(`ERROR! get dor data failed! ${rsp.msg}`);
      }

      return rsp;
    },
    {
      staleTime: 3 * (60 * 1000),
      suspense: false,
      refetchOnWindowFocus: false,
      enabled: isOpenDorDialog,
      refetchOnReconnect: false,
    },
  );
}

export function useSetLocationMappingAPI() {
  const { handleReqError } = useError();
  const { personnel } = useAppSelector(getAuthenticateUser);
  const dispatch = useAppDispatch();

  const setLocationMapping = async (data: Omit<TLocationsMappingReq, 'shopName'>) => {
    const payload = {
      ...data,
      shopName: personnel.shopName,
    };

    return DorController().setLocationsMapping<IBaseResponse>(payload);
  };

  return useMutation(setLocationMapping, {
    onSuccess: async (rsp) => {
      if (rsp?.success) {
        dispatch(settingsActions.setIsOpenDorDialog(false));
      } else if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error);
      } else if ('msg' in rsp) {
        console.error(`ERROR! set location mapping failed! ${rsp.msg}`);
      }
    },
    onError: (error: TAxiosError) => {
      console.error(`ERROR! set location mapping request threw an Exception! ${error}`);
    },
  });
}

type TGetMetrics = {
  dates?: [Moment, Moment],
  isMainMetricsPage?: boolean
};

export function useGetLocationMetricsAPI(options?: TGetMetrics) {
  const dispatch = useAppDispatch();

  const { personnel, shop } = useAppSelector(getAuthenticateUser);
  const integrations = useAppSelector(getIntegrations);
  const posthog = usePostHog();

  const { handleReqError } = useError();

  const byDay = !options?.dates;

  const { fromDate, toDate } = getDatesRange(
    options?.dates,
    false,
    byDay,
    shop?.shopTimezone,
  );

  return useQuery(
    [QUERY_KEYS.INTEGRATIONS.GET_METRICS],
    async () => {
      const data = {
        shopName: personnel.shopName,
        from: fromDate,
        to: toDate,
        interval: 'hour',
        timezone: shop?.shopTimezone,
      };

      const rsp = await DorController().getLocationMetrics<TGetDorLocationMetrics>(data);

      if (rsp?.success && 'locationMetrics' in rsp) {
        dispatch(ordersActions.setLocationMetrics(rsp.locationMetrics));

        posthog.capture('ManageMate_web', {
          shopName: personnel?.shopName,
          newMember: personnel?.email,
          addedAt: new Date().toISOString(),
          action: 'user_set_location_mapping',
        });

        return rsp;
      }

      if (rsp?.error && rsp.error?.response?.status) {
        handleReqError(rsp.error, data);
      } else if ('msg' in rsp) {
        console.error(`ERROR! get location metrics failed! ${rsp.msg}`);
      }

      return rsp;
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      suspense: false,
      staleTime: 5 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      enabled: (integrations?.Dor?.integrated && options?.isMainMetricsPage) || false,
    },
  );
}
